  import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
  import { HouseContext } from '../components/HouseContext';
  import CountryDropdown from '../components/CountryDropdown';
  import PropertyDropdown from '../components/PropertyDropdown';
  import PriceRangeDropdown from '../components/PriceRangeDropdown';
  import BedroomsDropdown from '../components/BedroomsDropdown';
  import Pagination from '../components/Pagination';
  import { ImSpinner2 } from 'react-icons/im';
  import { RiSearch2Line } from 'react-icons/ri';
  import { IoIosArrowDown } from "react-icons/io";
  import office from '../assets/img/hoselist1.webp';
  import '../styles/HouseList.css';
  import '../styles/House.css';
  import Header from '../components/Header';
  import Footer from '../components/Footer';
  import { gsap } from 'gsap';
  import { motion, useScroll, useTransform } from 'framer-motion';
  import { ScrollTrigger } from "gsap/ScrollTrigger";
  import FilteredProperties from './FilteredProperties';

  gsap.registerPlugin(ScrollTrigger);

  //! ----------------------------> MAPPING THROUGH DATA HOUSES
  //*context from variables and functions that filters data
  const HouseList = () => {
    const searchBarRef = useRef(null);
    const propertiesSectionRef = useRef(null);
    const titleTop = useRef(null);
    const heroTextRef = useRef(null);
    const heroDescRef = useRef(null);
    const arrowBtnContainerRef = useRef(null);
    const filteredPropertiesRef = useRef(null);
    const housesDisplayRef = useRef(null);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  const {
    houses,
    loading,
  } = useContext(HouseContext); 

  //! ================= >>> UI/UX REFS <<<<< =======
    const [searching, setSearching] = useState(false);
    const [filteredHouses, setFilteredHouses] = useState(houses);
    const [waitingForResults, setWaitingForResults] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState('Price range (any)');
    const [selectedBedrooms, setSelectedBedrooms] = useState('Bedrooms');
    //? id of the filter dropdown that is currently expanded (only one at a time)
    const [openDropdown, setOpenDropdown] = useState(null);

  //! ================= >>> PAGINATIONS <<<<< =======
    const [currentPage, setCurrentPage] = useState(1);
    const housesPerPage = 10;
    const totalHouses = filteredHouses.length;
    const totalPages = Math.ceil(totalHouses / housesPerPage);

    const indexOfLastHouse = currentPage * housesPerPage;
    const indexOfFirstHouse = indexOfLastHouse - housesPerPage;
    const currentHouses = filteredHouses.slice(indexOfFirstHouse, indexOfLastHouse);

    const [, setIsVisible] = useState(false);

  //! ================= >>> ANIMATIONS <<<<< =======


  const { scrollYProgress } = useScroll({
    target: propertiesSectionRef, // Folosim propertiesSectionRef ca target
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-40%", "20%"]);


    useEffect(() => {
      const handleScroll = () => {
        const scrollPosition = window.scrollY;
        const windowHeight = window.innerHeight;
        const propertiesSectionHeight = propertiesSectionRef.current.offsetHeight;
        const propertiesSectionTop = propertiesSectionRef.current.offsetTop;
        const triggerScrollThreshold = propertiesSectionTop + propertiesSectionHeight * 1.3;

        if (scrollPosition + windowHeight >= triggerScrollThreshold) {
          setIsVisible(true);
        }
      };

      // Create the main animation timeline
      const tl = gsap.timeline();

      // Title animation
      tl.fromTo(titleTop.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
      );

      // Hero text animation
      tl.fromTo(heroTextRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        "-=0.4" // Overlap with previous animation
      );

      // Description animation
      tl.fromTo(heroDescRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        "-=0.3" // Overlap with previous animation
      );

      // Button container animation
      tl.fromTo(arrowBtnContainerRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: 'power3.out' },
        "-=0.2" // Overlap with previous animation
      );

      if (window.innerWidth > 768) {
        setIsVisible(true);
      }

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    useEffect(() => {
      if (window.innerWidth <= 768) {
        gsap.fromTo(searchBarRef.current,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }
        );
      }
    }, []);


  //! ================= >>> BTN ANIMATION <<< ============
    const arrowBtnRef = useRef(null);

    useEffect(() => {
      if (arrowBtnRef.current) {
        const circle = arrowBtnRef.current.querySelector('circle');
        if (circle) {
          const length = circle.getTotalLength();

          circle.style.strokeDasharray = length;
          circle.style.strokeDashoffset = length;

          gsap.to(circle, {
            strokeDashoffset: 0,
            duration: 1,
            delay: 0.8,
            ease: 'power2.inOut',
          });
        }

        // Add a pulse animation to the arrow button
        gsap.to(arrowBtnRef.current, {
          scale: 1.05,
          repeat: -1,
          yoyo: true,
          duration: 1.5,
          ease: 'power1.inOut'
        });
      }
    }, []);

  //! ================= >>> DROPDOWN STATE MANAGEMENT <<< ============
    //* The dropdown panels expand in-flow (see FilterDropdown), so the only
    //* coordination needed is "one open at a time".
    const closeDropdowns = useCallback(() => setOpenDropdown(null), []);
    const toggleDropdown = useCallback((id) => {
        setOpenDropdown((current) => (current === id ? null : id));
    }, []);
    const dropdownProps = (id) => ({
        isOpen: openDropdown === id,
        onToggle: () => toggleDropdown(id),
        onClose: closeDropdowns,
    });

  //! ================= >>> FILTERING <<<<< =======
  const [selectedCountry, setSelectedCountry] = useState('Location (any)');
  const [selectedProperty, setSelectedProperty] = useState('Property (any)');

  const applyFilters = () => {
    setWaitingForResults(true);
    setTimeout(() => {
        let newFilteredHouses = [...houses];

        //? Filtru pentru țară
        if (selectedCountry !== 'Location (any)' && selectedCountry !== 'Location' && selectedCountry !== '(any)') {
            newFilteredHouses = newFilteredHouses.filter((house) => house.country === selectedCountry);
        }

        //? Filtru pentru tipul de proprietate
        if (selectedProperty !== 'Property (any)' && selectedProperty !== 'Property' && selectedProperty !== '(any)') {
            newFilteredHouses = newFilteredHouses.filter((house) => house.type === selectedProperty);
        }

        //? Filtru pentru interval de preț
        if (selectedPrice !== 'Price range (any)' && selectedPrice !== 'Price range' && selectedPrice !== '(any)') {
            const priceRange = selectedPrice.split('-');
            if (priceRange.length === 2) {
                const min = parseInt(priceRange[0]);
                const max = parseInt(priceRange[1]);
                newFilteredHouses = newFilteredHouses.filter((house) => {
                    const housePrice = parseInt(house.price);
                    return housePrice >= min && housePrice <= max;
                });
            }
        }

        //? Filtru pentru dormitoare
        if (selectedBedrooms !== 'Bedrooms (any)' && selectedBedrooms !== 'Bedrooms' && selectedBedrooms !== '(any)') {
            newFilteredHouses = newFilteredHouses.filter((house) => house.bedrooms.toString() === selectedBedrooms);
        }

        setFilteredHouses(newFilteredHouses);
        setCurrentPage(1); //! Resetăm la prima pagină când se aplică filtrele
        setSearching(false);
        setWaitingForResults(false);
    }, 600);
  };

  useEffect(() => {
      if (searching) {
        applyFilters();
        handleSearchClick();
      }
      // applyFilters closes over the selected filters, which are all listed below
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, selectedCountry, selectedProperty, selectedBedrooms, selectedPrice]);


  const handleSearchClick = () => {
      setSearching(true);
  };

  const handleCountryChange = (country) => {
    //? Adaugă '(any)' la valoare dacă e doar valoarea simplă
    const formattedCountry = country === '(any)' ? 'Location (any)' : country;
    setSelectedCountry(formattedCountry);
  };

  const handlePropertyChange = (property) => {
    //? Adaugă '(any)' la valoare dacă e doar valoarea simplă
    const formattedProperty = property === '(any)' ? 'Property (any)' : property;
    setSelectedProperty(formattedProperty);
  };

  const handlePriceChange = (price) => {
    //? Adaugă 'Price range' la valoare dacă e doar valoarea simplă
    const formattedPrice = price === '(any)' ? 'Price range (any)' : price;
    setSelectedPrice(formattedPrice);
  };

  const handleBedroomsChange = (bedrooms) => {
    //? Adaugă 'Bedrooms' la valoare dacă e doar valoarea simplă
    const formattedBedrooms = bedrooms === '(any)' ? 'Bedrooms (any)' : bedrooms;
    setSelectedBedrooms(formattedBedrooms);
  };

    const scrollToNextSection = () => {
      if (housesDisplayRef.current) {
        housesDisplayRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const scrollToTop = () => {
      const element = document.getElementById('filtered-properties-section');
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth',
        });
      }
    };


    const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
      // applyFilters(selectedBedrooms, selectedPrice);
      scrollToTop();
    };

    //* DROPDOWN MENU
    useEffect(() => {
      const handleMenuStateChange = (e) => {
        if (searchBarRef.current) {
          if (e.detail.isOpen) {
            // Când meniul este deschis
            searchBarRef.current.style.zIndex = '10'; 
          } else {
            // Când meniul este închis
            searchBarRef.current.style.zIndex = '220'; 
          }
        }
      };

      document.addEventListener('menu-state-changed', handleMenuStateChange);

      return () => {
        document.removeEventListener('menu-state-changed', handleMenuStateChange);
      };
    }, []);


    if (loading) {
      return <ImSpinner2 className='mx-auto animate-spin text-indigo-700 text-4xl mt-[200px]' />;
    }

    return (
      <section className='mt-50 mb-6 relative'>
        {/*INTRO*/}
        <section ref={propertiesSectionRef} className='houselist-header h-screen mb-2 bg-cover bg-center relative rounded-b-[1.5rem] overflow-hidden'>
          <div className='absolute top-0 left-0 w-full h-screen overflow-hidden rounded-b-[1.5rem] z-10'>
            <motion.div className="absolute w-full h-full rounded-b-[1.5rem] z-10" style={{ y }}>
              <img className='prop_filter_banner bg-cover bg-center' src={office} alt="" />
            </motion.div>
          </div>

          <div className="relative z-20 flex flex-col justify-center h-full">
            <Header className="mt-2 z-30" />

            <div className="flex flex-col items-center text-center hero_list w-full">
              <div className='f-y-h' ref={titleTop}>
                <h1 ref={heroTextRef} className='leading-[1] text-[3.5rem] font-normal tracking-tight text-white opacity-0'>
                  Find your home
                </h1>

                <div className='mt-2 p_desc'>
                  <p ref={heroDescRef} className='leading-[1] ml-6 text-white w-[370px] text-center opacity-0'>
                    You have the ability to search for your desired property based on your needs.
                  </p>
                </div>

                <div  ref={arrowBtnContainerRef} className="flex items-center justify-center w-full opacity-0">
                  <button ref={arrowBtnRef} className="arrow-btn rounded-full w-20 h-20 flex items-center justify-center mt-2 hover:bg-indigo-700 duration-500 hover:text-white" onClick={scrollToNextSection}>
                    <svg className="w-30 h-30 md:text-white" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" stroke="rgba(39, 68, 236, 0.767)" strokeWidth="1" fill="transparent" />
                    </svg>
                    <IoIosArrowDown className="absolute w-10 h-10 text-white" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </section>


        {/*PROPERTY DISPLAY*/}
        <div id='houses_display' ref={housesDisplayRef} className='mx-auto mt-24 mb-8 relative xl:mx-auto lg:mx-auto md:mx-auto sm:mx-auto'>
          <div ref={searchBarRef} className='search-bar backdrop-blur'>
            <div className="filter-row">
                <CountryDropdown onChange={handleCountryChange} {...dropdownProps('country')} />
                <PropertyDropdown onChange={handlePropertyChange} {...dropdownProps('property')} />
                <PriceRangeDropdown onChange={handlePriceChange} {...dropdownProps('price')} />
                <BedroomsDropdown onChange={handleBedroomsChange} {...dropdownProps('bedrooms')} />

                <button
                    type="button"
                    onClick={handleSearchClick}
                    className='filter-search-btn bg-indigo-700 hover:bg-indigo-800 transition rounded-full
                    flex justify-center items-center text-white'
                >
                    <RiSearch2Line className="mr-2" />
                    Search
                </button>
            </div>
          </div>

          {waitingForResults ? (
            <ImSpinner2 className='mx-auto animate-spin text-indigo-700 text-4xl mt-4' />
          ) : (
            <>
              <div ref={filteredPropertiesRef} id="filtered-properties-section">
                  <FilteredProperties currentHouses={currentHouses} />
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
        </div>
        <Footer />
      </section>
    );
  };

  export default HouseList;